import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon } from '../icons/icons';

type ModalProps = {
  visible: boolean;
  toggle?: () => void;
  modalContent: JSX.Element | ((closeModal: () => void) => React.ReactNode);
  toggleable?: boolean;
  customClass?: string;
  headerClass?: string;
  headerText?: string;
};

export const Modal = ({
  visible,
  toggle = () => {},
  modalContent,
  toggleable = true,
  customClass,
  headerText,
}: ModalProps) => {
  const [isClosing, setIsClosing] = useState(false);
  const [isOpening, setIsOpening] = useState(false);

  useEffect(() => {
    if (visible) {
      setIsOpening(true);
      setIsClosing(false);
      document.body.style.overflow = 'hidden';
    } else {
      setIsOpening(false);
      document.body.style.overflow = 'auto';
    }
  }, [visible]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(toggle, 300); // Match animation duration
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && toggleable) {
      handleClose();
    }
  };

  return visible
    ? createPortal(
      <div
          className={classNames(
            'fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4',
            'bg-black/30 backdrop-blur-[2px] transition-all duration-300',
            {
              'opacity-100': isOpening,
              'opacity-0': isClosing,
            },
          )}
          onClick={handleBackdropClick}
          style={{ height: '100vh' }}
      >
        <div
            className={classNames(
              'bg-white rounded-lg shadow-xl w-full max-w-3xl relative',
              'transition-all duration-300 transform',
              {
                'opacity-100 scale-100 translate-y-0': isOpening,
                'opacity-0 scale-95 translate-y-4': isClosing,
              },
              'flex flex-col max-h-[95vh] md:max-h-[90vh]',
              customClass,
            )}
            role='dialog'
            aria-modal='true'
        >
          <div
              className={classNames(
                'relative px-4 py-2 md:px-6 flex items-center justify-between w-full border-b border-gray-200 flex-shrink-0',
              )}
          >
            <p className='text-sm md:text-md font-medium w-full'>
              { headerText }
            </p>
            { toggleable && (
              <button
                  aria-label='close-modal'
                  className='p-1.5 md:p-2 rounded-full text-red-700 transition-all duration-200 ease-in-out hover:bg-red-300/40 bg-red-400/40 hover:rotate-90 focus:outline-none focus:ring-2 focus:ring-blue-500 flex-shrink-0'
                  onClick={handleClose}
              >
                <CloseIcon />
              </button>
            ) }
          </div>
          <div className='p-3 md:p-6 overflow-y-auto flex-1'>
            { typeof modalContent === 'function'
              ? modalContent(handleClose)
              : modalContent }
          </div>
        </div>
      </div>,
      document.body,
    )
    : null;
};
